/**
 * Ends an idle session, having asked first.
 *
 * Mounted once in `App`, alongside the announcement host, so it covers
 * every authenticated screen including the landing page — which builds
 * its own layout and would be missed by a SiteLayout-only mount.
 *
 * Runs only for a signed-in person, and never on the auth pages
 * themselves: there is no session to protect on /login, and warning
 * someone that they are about to be signed out of the sign-in form would
 * be nonsense.
 *
 * On expiry the current path is carried through as `?next=`, so
 * returning is one sign-in rather than a hunt back to where they were,
 * along with `reason=idle` so the sign-in screen can say what happened.
 * The idle clock runs whether or not this tab is in front, which means
 * the warning is sometimes spent where nobody sees it — an unexplained
 * login form reads as a lost session rather than a protected one.
 *
 * Pressing "Sign Out Now" carries no reason: nobody needs telling why
 * they are looking at a login form they asked for.
 *
 * It also drives token renewal, because the two are one policy: short
 * tokens limit what a leaked one is worth, renewal stops that costing
 * anyone their afternoon, and the idle timeout stops renewal quietly
 * keeping an abandoned laptop signed in forever.
 */

import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { IdleWarningDialog } from './IdleWarningDialog';
import { useAuth } from './useAuth';
import { useIdleTimeout, WARN_MS } from './useIdleTimeout';
import { useTokenRenewal } from './useTokenRenewal';

/** Pages with no session to guard. */
const AUTH_PATHS = ['/login', '/auth/login', '/signup', '/auth/signup', '/forgot-password'];

export function SessionGuard() {
  const { user, isReady, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onAuthPage = AUTH_PATHS.some((p) => location.pathname.startsWith(p));
  const enabled = isReady && !!user && !onAuthPage;

  // Two halves of one policy: the token is short so a leaked one dies
  // quickly, and renewal keeps a working session from noticing. The idle
  // timeout below is what stops renewal propping up an abandoned one.
  useTokenRenewal(isReady && !!user);

  const signOut = useCallback(
    (returnTo: string, reason?: 'idle') => {
      logout();
      // The clock is deliberately unconditional — it keeps running while
      // this tab is in the background, which is how every business
      // platform treats an idle session. The cost is that the warning
      // may be spent in a tab nobody is looking at, so the sign-in
      // screen has to be the thing that explains itself. Without the
      // reason, coming back to a login form reads as a bug.
      const params = new URLSearchParams({ next: returnTo });
      if (reason) params.set('reason', reason);
      navigate(`/login?${params}`, { replace: true });
    },
    [logout, navigate],
  );

  const { warning, secondsLeft, stayActive } = useIdleTimeout(
    enabled,
    // Read the path at expiry rather than closing over it, so the value
    // is wherever they actually were when the clock ran out.
    useCallback(
      () => signOut(window.location.pathname + window.location.search, 'idle'),
      [signOut],
    ),
  );

  if (!enabled) return null;

  return (
    <IdleWarningDialog
      open={warning}
      secondsLeft={secondsLeft}
      totalSeconds={Math.round(WARN_MS / 1000)}
      onStay={stayActive}
      onSignOut={() => signOut(location.pathname + location.search)}
    />
  );
}
